import React, { useMemo, useState } from 'react';
import { Button } from '@/ui/components/Button';
import { TextField } from '@/ui/components/TextField';
import { useNotificationStore } from '@/store/notificationStore';
import { WalletSigningModal } from '@/components/transactions/WalletSigningModal';
import { toSigningPayload } from '@/lib/wallet';
import type { SigningPayload } from '@/types/signing';
import {
  createAuction,
  confirmAuctionListing,
  type CreateAuctionResponse,
} from '@/api/auctions';

const DROPS_PER_XRP = 1_000_000;
const MIN_DURATION_MINUTES = 30;
const MAX_DURATION_DAYS = 30;

interface Props {
  nftTokenId: string;
  /** Called after the seller signs and the listing is confirmed on chain. */
  onConfirmed?: () => void;
  /** Called when the user clicks Cancel. */
  onCancel?: () => void;
}

function defaultExpiresAt(): string {
  // 7 days from now, rounded to the next minute, formatted as datetime-local.
  const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const AuctionListingForm: React.FC<Props> = ({ nftTokenId, onConfirmed, onCancel }) => {
  const [minBid, setMinBid] = useState('');
  const [reserve, setReserve] = useState('');
  const [expiresAtLocal, setExpiresAtLocal] = useState(defaultExpiresAt);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Show validation message only after the user has interacted with the form
  // — avoids "Enter a valid minimum bid" flashing on the empty initial render.
  const [touched, setTouched] = useState(false);

  const [signing, setSigning] = useState<{
    auctionId: string;
    payload: SigningPayload;
  } | null>(null);

  const { success: showSuccess, error: showError } = useNotificationStore();

  const validation = useMemo(() => {
    const parsedMin = Number(minBid);
    const parsedReserve = Number(reserve);
    const expires = new Date(expiresAtLocal);
    const now = Date.now();

    if (!Number.isFinite(parsedMin) || parsedMin <= 0) return 'Enter a valid minimum bid';
    if (!Number.isFinite(parsedReserve) || parsedReserve <= 0) return 'Enter a valid reserve price';
    if (parsedReserve < parsedMin) return 'Reserve price must be ≥ minimum bid';
    if (parsedReserve < 1) return 'Reserve must be at least 1 XRP';
    if (Number.isNaN(expires.getTime())) return 'Pick a valid end time';
    if (expires.getTime() < now + MIN_DURATION_MINUTES * 60 * 1000)
      return `Auction must run at least ${MIN_DURATION_MINUTES} minutes`;
    if (expires.getTime() > now + MAX_DURATION_DAYS * 24 * 60 * 60 * 1000)
      return `Auction may run at most ${MAX_DURATION_DAYS} days`;
    return null;
  }, [minBid, reserve, expiresAtLocal]);

  async function handleSubmit() {
    setError(null);
    if (validation) {
      setError(validation);
      return;
    }
    setSubmitting(true);
    try {
      const minBidDrops = BigInt(Math.round(Number(minBid) * DROPS_PER_XRP));
      const reserveDrops = BigInt(Math.round(Number(reserve) * DROPS_PER_XRP));
      const expiresIso = new Date(expiresAtLocal).toISOString();

      const res = (await createAuction({
        nftTokenId,
        minBidDrops: minBidDrops.toString(),
        reservePriceDrops: reserveDrops.toString(),
        expiresAt: expiresIso,
      })) as CreateAuctionResponse;

      if (!res.success || !res.data) {
        setError(res.error || 'Failed to create auction');
        return;
      }

      setSigning({
        auctionId: res.data.auction.id,
        payload: toSigningPayload(res.data.signingIntent ?? res.data.payload),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create auction');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSigned(txHash: string) {
    if (!signing) return;
    const res = await confirmAuctionListing(signing.auctionId, txHash);
    if (res.success) {
      showSuccess('Your NFT is now up for auction!');
      onConfirmed?.();
    } else {
      showError((res as any).error || 'Auction created but on-chain confirmation failed');
    }
    setSigning(null);
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <TextField className="h-auto w-full flex-none" label="Minimum bid (XRP)" helpText="Lowest bid we'll accept">
        <TextField.Input
          type="number"
          inputMode="decimal"
          placeholder="e.g., 80"
          value={minBid}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setMinBid(e.target.value); setTouched(true); }}
        />
      </TextField>
      <TextField className="h-auto w-full flex-none" label="Reserve price (XRP)" helpText="Auction settles only if a bid hits this">
        <TextField.Input
          type="number"
          inputMode="decimal"
          placeholder="e.g., 150"
          value={reserve}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setReserve(e.target.value); setTouched(true); }}
        />
      </TextField>
      <TextField className="h-auto w-full flex-none" label="End time" helpText="When bidding closes">
        <TextField.Input
          type="datetime-local"
          value={expiresAtLocal}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setExpiresAtLocal(e.target.value); setTouched(true); }}
        />
      </TextField>

      {(error || (touched && validation)) && (
        <span className="text-caption font-caption text-error-700">{error || validation}</span>
      )}

      <span className="text-caption font-caption text-subtext-color">
        At settlement, you'll receive the winning bid minus the 1% xrp.deals fee.
      </span>

      <div className="flex w-full items-center gap-2">
        <Button
          className="h-10 w-auto flex-none grow"
          variant="neutral-secondary"
          size="large"
          onClick={() => onCancel?.()}
        >
          Cancel
        </Button>
        <Button
          className="h-10 w-auto flex-none grow"
          size="large"
          onClick={handleSubmit}
          disabled={submitting || validation !== null}
        >
          {submitting ? 'Creating…' : 'List for Auction'}
        </Button>
      </div>

      {signing && (
        <WalletSigningModal
          open={true}
          onClose={() => setSigning(null)}
          title="List for Auction"
          payload={signing.payload}
          onSuccess={handleSigned}
          onRejected={() => {
            showError('Auction listing rejected');
            setSigning(null);
          }}
          onExpired={() => {
            showError('Sign request expired');
            setSigning(null);
          }}
        />
      )}
    </div>
  );
};
